import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';

import {Button} from '@betareader/design-system';
import { useTranslation } from 'react-i18next';

import {
  accessUtil, authUtil
} from '../../utils';
import {
  toggleFollowStatus
} from '../../modules/book';

import { CenteredContent } from '../../style';

import EditBookDetails from './detail/editBookDetails';
import ViewBookDetails from './detail/viewBookDetails';
import BookFormatUpgrade from './detail/bookFormatUpgrade';
import BookFollowButton from './followers/bookFollowButton';
import Cover, { getCoverImage } from './bookCover/coverWrapper';

const MetaData = styled.div`
  background-color: var(--br-surface);
  padding: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  font-size: 1em;
  border: 1px solid var(--br-border);
  border-radius: 5px;
  color: var(--br-text);
  .metadata-stats {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
  }
  .metadata-badge {
    border-radius: var(--br-control-radius);
    padding: 3px 8px;
    background: var(--br-raised-surface);
    border: 1px solid var(--br-border);
  }
  .metadata-badge[data-status='alpha'] {
    background: var(--br-warning-surface);
    border-color: var(--br-warning-border);
  }
  .metadata-badge[data-status='beta'] {
    background: var(--br-success-surface);
    border-color: var(--br-success-border);
  }
`;

const BookOverview = ({}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const currentBook = useSelector(state => state.book.currentBook);
  const followStatus = useSelector(state => state.book.followStatus);


  const canEdit = useCallback(() => {
    return accessUtil.isAllowed(currentBook.access, ['edit']);
  }, [currentBook?.access]);

  const isAuthorOrCollaborator = useCallback(() => ['author', 'collaborator'].includes(currentBook.role), [currentBook?.role]);

  const goToSignIn = () => {
    history.push(`/signin?redirect=${encodeURIComponent(location.pathname + location.search)}`);
  }

  const toggleFollowBook = async (action) => {
    const idToken = await authUtil.getFreshIdToken();
    if (!idToken) {
      // get the user signed in
      goToSignIn();
      return;
    }
    if (isAuthorOrCollaborator()) {
      return; // authors can't follow their own books
    }
    if (action) {
      dispatch(toggleFollowStatus({ idToken, bookId: currentBook._id, action }))
      return;
    }
    // no action provided, just toggle status
    if (followStatus[currentBook._id] && followStatus[currentBook._id].following) {
      dispatch(toggleFollowStatus({ idToken, bookId: currentBook._id, action: 'unfollow' }));
    } else {
      dispatch(toggleFollowStatus({ idToken, bookId: currentBook._id, action: 'follow' }));
    }
  }

  // rendering
  if (!currentBook) {
    return null;
  }
  const cover = getCoverImage(currentBook);

  return (
    <>
      <Helmet>
        <meta charSet='utf-8' />
        <title>{currentBook.title}</title>
        <meta property='og:title' content={`${t('Read')} ${currentBook.title} ${t('by')} ${currentBook.authorName} ${t('onBetaReader')}`} />
        <meta property='og:site_name' content='BetaReader.io' />
        <meta property='og:description' content={currentBook.description} />
        <meta property='og:image' content={cover} />
      </Helmet>
      <div>
        <Cover book={currentBook} editable={canEdit()} />
        <CenteredContent>
          <MetaData className='metadata-bar'>
            <div className='metadata-stats'>
              <span>{t('WordCountString', {count: currentBook.wordCount || 0})}</span>
              <span>{t('ChapterCount', {count: currentBook.chapterCount || 0})}</span>
              {currentBook.language && <span>{currentBook.language.charAt(0).toUpperCase() + currentBook.language.slice(1)}</span>}
              {currentBook.status && (
                <span className='metadata-badge' data-status={currentBook.status}
                  title={t(`MSStatusDescription_${currentBook.status}`)}>
                  {currentBook.status}
                </span>
              )}
              {currentBook.isMature && (
                <span className='metadata-badge' data-status='mature'>
                  {t('MatureContent')}
                </span>
              )}
            </div>
            {canEdit() ? (
              <Button size='small'
                onClick={() => history.push(`/books/${currentBook._id}/content`)}>
                {t('EditContent')}
              </Button>
            ) : (
              <BookFollowButton />
            )}
          </MetaData>
        </CenteredContent>
        <CenteredContent>
          {
            isAuthorOrCollaborator() && (
              <EditBookDetails />
            )
          }
          {
            !isAuthorOrCollaborator() && (
              <ViewBookDetails toggleFollowBook={toggleFollowBook} />
            )
          }
          {currentBook.role === 'author' && (
            <BookFormatUpgrade bookId={currentBook._id} />
          )}
        </CenteredContent>
      </div>
    </>
  );
}

export default BookOverview;
